import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRightIcon,
  CalendarDaysIcon,
  ChevronRightIcon,
  CloudArrowUpIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../hooks/useAuth';
import { LoginForm } from '../login/LoginForm';
import { eventsApi } from '../../api/eventsApi';
import { transcriptApi } from '../../api/transcriptApi';
import './home.css';

const ACTIVE_SESSION_STATUSES = new Set(['IN_PROGRESS', 'PROCESSING']);
const ACTIVE_EVENT_STATUSES = new Set(['ONGOING', 'IN_PROGRESS', 'ACTIVE']);
const RECENT_SESSIONS_LIMIT = 8;
const ACTIVE_EVENTS_LIMIT = 5;
const HOME_REQUEST_TIMEOUT_MS = 8000;

const getField = (item, ...keys) => {
  for (const key of keys) {
    if (item?.[key]) return item[key];
  }
  return undefined;
};

const getUserName = (user, userProfile) => {
  const profileName = getField(userProfile, 'full_name', 'fullName', 'name', 'username', 'email');
  if (profileName) return profileName;

  try {
    const parsed = JSON.parse(user);
    return getField(parsed, 'full_name', 'fullName', 'name', 'username', 'email') || 'User';
  } catch {
    return user || 'User';
  }
};

const normalizeStatus = (status) => String(status || '').toUpperCase();

const getSessionStatus = (session) => normalizeStatus(getField(
  session,
  'status',
  'transcript_status',
  'transcriptStatus'
));

const getDate = (item) => {
  const value = getField(item, 'updated_at', 'updatedAt', 'processing_completed_at', 'processingCompletedAt', 'created_at', 'createdAt');
  return value ? new Date(value) : null;
};

const formatRelative = (dateValue) => {
  if (!dateValue) return '—';

  const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
  if (Number.isNaN(date.getTime())) return '—';

  const diffSeconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
  if (diffSeconds < 60) return 'just now';
  if (diffSeconds < 3600) return `${Math.floor(diffSeconds / 60)}m ago`;
  if (diffSeconds < 86400) return `${Math.floor(diffSeconds / 3600)}h ago`;
  if (diffSeconds < 86400 * 7) return `${Math.floor(diffSeconds / 86400)}d ago`;

  return date.toLocaleDateString();
};

const getStatusClass = (status) => {
  const normalized = normalizeStatus(status);
  if (normalized === 'COMPLETED') return 'completed';
  if (normalized === 'PUBLISHED') return 'published';
  if (ACTIVE_SESSION_STATUSES.has(normalized) || ACTIVE_EVENT_STATUSES.has(normalized)) return 'live';
  if (normalized === 'FAILED' || normalized === 'CANCELLED') return 'danger';
  return 'neutral';
};

export default function Home() {
  const { user, userProfile } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [events, setEvents] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [sessionsError, setSessionsError] = useState(null);
  const [eventsError, setEventsError] = useState(null);

  useEffect(() => {
    if (!user) return;

    let cancelled = false;
    const sessionsController = new AbortController();
    const eventsController = new AbortController();
    const sessionsTimeout = window.setTimeout(() => sessionsController.abort(), HOME_REQUEST_TIMEOUT_MS);
    const eventsTimeout = window.setTimeout(() => eventsController.abort(), HOME_REQUEST_TIMEOUT_MS);

    const loadSessions = async () => {
      try {
        setLoadingSessions(true);
        setSessionsError(null);

        const sessionsData = await transcriptApi.getSessions(
          { page: 1, per_page: RECENT_SESSIONS_LIMIT, ordering: 'date_desc', compact: true, include_total: false },
          { signal: sessionsController.signal }
        );
        if (cancelled) return;
        setSessions(Array.isArray(sessionsData?.sessions)
          ? sessionsData.sessions
          : (Array.isArray(sessionsData?.results) ? sessionsData.results : []));
      } catch (error) {
        if (cancelled) return;
        console.error('Failed to load recent sessions:', error);
        setSessionsError('Recent sessions are taking too long to load.');
        setSessions([]);
      } finally {
        if (!cancelled) setLoadingSessions(false);
      }
    };

    const loadEvents = async () => {
      try {
        setLoadingEvents(true);
        setEventsError(null);

        const eventsData = await eventsApi.getAll(
          { skip: 0, limit: ACTIVE_EVENTS_LIMIT, status_filter: 'ONGOING' },
          { signal: eventsController.signal }
        );
        if (cancelled) return;
        setEvents(Array.isArray(eventsData?.events)
          ? eventsData.events
          : (Array.isArray(eventsData?.results) ? eventsData.results : []));
      } catch (error) {
        if (cancelled) return;
        console.error('Failed to load active events:', error);
        setEventsError('Active events are taking too long to load.');
        setEvents([]);
      } finally {
        if (!cancelled) setLoadingEvents(false);
      }
    };

    loadSessions();
    loadEvents();

    return () => {
      cancelled = true;
      window.clearTimeout(sessionsTimeout);
      window.clearTimeout(eventsTimeout);
      sessionsController.abort();
      eventsController.abort();
    };
  }, [user]);

  const dashboard = useMemo(() => {
    const activeEvents = events.filter(event => ACTIVE_EVENT_STATUSES.has(normalizeStatus(getField(event, 'status'))));

    const recentSessions = [...sessions]
      .sort((a, b) => (getDate(b)?.getTime() || 0) - (getDate(a)?.getTime() || 0))
      .slice(0, RECENT_SESSIONS_LIMIT);

    const ongoingEvents = [...activeEvents]
      .sort((a, b) => (getDate(b)?.getTime() || 0) - (getDate(a)?.getTime() || 0))
      .slice(0, ACTIVE_EVENTS_LIMIT);

    return {
      ongoingEvents,
      recentSessions,
    };
  }, [events, sessions]);

  if (!user) {
    return (
      <LoginForm
        onSSOLogin={() => {}}
        ssoLoading={false}
        ssoError={null}
      />
    );
  }

  const displayName = getUserName(user, userProfile);
  const firstName = displayName.split(/\s+/)[0] || 'there';
  const todayLabel = new Date().toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  });
  const quickOptions = [
    {
      title: 'Sessions',
      description: 'Review transcripts and processing status.',
      to: '/sessions',
      icon: DocumentTextIcon,
    },
    {
      title: 'Events',
      description: 'Manage events connected to sessions.',
      to: '/events',
      icon: CalendarDaysIcon,
    },
    {
      title: 'Upload',
      description: 'Add a new transcript or media file.',
      to: '/upload',
      icon: CloudArrowUpIcon,
    },
  ];

  return (
    <div className="home-dashboard">
      <div className="home-content">
        <div className="home-page-head">
          <div>
            <h1 className="home-page-title">Welcome back, {firstName}.</h1>
            <p className="home-page-sub">
              {todayLabel} · Recent sessions and active events
            </p>
          </div>
        </div>

        <div className="home-quick-options">
          {quickOptions.map((option) => {
            const QuickIcon = option.icon;

            return (
              <Link key={option.to} to={option.to} className="home-quick-card">
                <div className="home-quick-icon">
                  <QuickIcon />
                </div>
                <div>
                  <h2>{option.title}</h2>
                  <p>{option.description}</p>
                </div>
                <span>Open <ArrowRightIcon /></span>
              </Link>
            );
          })}
        </div>

        <div className="home-grid">
          <section>
            <div className="home-section-head">
              <h2>Recent activity</h2>
              <Link to="/sessions">View all <ArrowRightIcon /></Link>
            </div>
            <div className="home-card">
              {loadingSessions ? (
                <div className="home-loading-row home-empty-card">
                  <div className="home-spinner" />
                  <span>Loading recent activity...</span>
                </div>
              ) : sessionsError ? (
                <div className="home-empty-row home-empty-card">
                  <DocumentTextIcon />
                  <span>{sessionsError}</span>
                </div>
              ) : dashboard.recentSessions.length === 0 ? (
                <div className="home-empty-row home-empty-card">
                  <DocumentTextIcon />
                  <span>No sessions yet</span>
                </div>
              ) : dashboard.recentSessions.map(session => {
                const sessionId = getField(session, 'id', 'session_id', 'sessionId');
                const status = getSessionStatus(session) || 'PENDING';
                const title = getField(session, 'title', 'original_filename', 'originalFilename') || 'Untitled session';
                const eventTitle = getField(session, 'event_title', 'eventTitle') || 'No event';
                const userName = getField(session, 'user_name', 'userName', 'user_email', 'userEmail') || 'Unknown user';

                return (
                  <Link key={sessionId} to={`/sessions/${sessionId}`} className="home-activity-row">
                    <div className={`home-act-dot home-status-${getStatusClass(status)}`} />
                    <div className="home-act-body">
                      <div className="home-act-title">{title}</div>
                      <div className="home-act-meta">
                        <span>{eventTitle}</span>
                        <span>·</span>
                        <span>{userName}</span>
                        <span>·</span>
                        <span>{formatRelative(getDate(session))}</span>
                      </div>
                    </div>
                    <span className={`home-badge home-badge-${getStatusClass(status)}`}>
                      <span className="home-badge-dot" />{status.replaceAll('_', ' ')}
                    </span>
                  </Link>
                );
              })}
            </div>
          </section>

          <aside className="home-sidebar" aria-label="Active events">
            <div className="home-side-section">
              <div className="home-section-head">
                <h2>Active events</h2>
                <Link to="/events">All <ArrowRightIcon /></Link>
              </div>
              <div className="home-events-list">
                {loadingEvents ? (
                  <div className="home-event-mini">
                    <div className="home-loading-inline">
                      <div className="home-spinner" />
                      <span>Loading active events...</span>
                    </div>
                  </div>
                ) : eventsError ? (
                  <div className="home-event-mini">
                    <div>
                      <div className="home-event-title">Active events unavailable</div>
                      <div className="home-event-meta">{eventsError}</div>
                    </div>
                  </div>
                ) : dashboard.ongoingEvents.length === 0 ? (
                  <div className="home-event-mini">
                    <div>
                      <div className="home-event-title">No active events</div>
                      <div className="home-event-meta">Ongoing events will show here.</div>
                    </div>
                  </div>
                ) : dashboard.ongoingEvents.map(event => {
                  const eventId = getField(event, 'id');
                  const title = getField(event, 'title') || 'Untitled event';
                  const sessionsCount = Number(getField(event, 'session_count', 'sessionCount', 'sessions_count', 'sessionsCount') || 0);

                  return (
                    <Link key={eventId} to={`/events/${eventId}`} className="home-event-mini">
                      <div>
                        <div className="home-event-org">{getField(event, 'organization', 'org', 'status') || 'Event'}</div>
                        <div className="home-event-title">{title}</div>
                        <div className="home-event-meta">{sessionsCount} sessions · {formatRelative(getDate(event))}</div>
                      </div>
                      <ChevronRightIcon />
                    </Link>
                  );
                })}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
